import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import TableHeader from './TableHeader';
import {
  getWeekDates,
  isBeforeToday,
  getPreviousWeekMonday,
  getCurrWeekMonday,
  getNextMonday,
} from '../../utils/utils';
import TableDateRow from './TableDateRow';
import TableSessionRow from './TableSessionRow';
import { Session } from '../../utils/types';
import { getTutorWeeklySessionsByStartEndDate } from '../../utils/network';
import { useUserContext } from '../../providers/UserContext';
import { useMixpanelContext } from '../../providers/MixpanelContext';
import OvalLoader from '../../assets/loader/oval';

function Schedule() {
  const [selectedDates, setSelectedDates] = useState<dayjs.Dayjs[]>([]);
  const [weeklySessions, setWeeklySessions] = useState<Session[]>([]);
  const { tutorByIesId, token } = useUserContext();
  const { mixpanelTrackEvent } = useMixpanelContext();
  const deviceOffset = -new Date().getTimezoneOffset() / 60;
  useEffect(() => {
    mixpanelTrackEvent('PageView', {
      PageName: 'TutorSchedulePageView',
      tutor: tutorByIesId,
    });
  }, []);

  const filterAndSortSessions = (sessions: Session[]): Session[] => {
    const now = new Date();
    const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);
    if (sessions.length > 0) {
      return (
        sessions
          // Filter sessions
          .filter((session) => {
            const startTime = new Date(session.date);
            const endTime = new Date(startTime.getTime() + session.duration * 60 * 1000);
            return endTime > fifteenMinutesAgo;
          })
          // Sort sessions by start time (date)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      );
    }
    return sessions;
  };

  const getWeeklySessionsByTutorIdStartEnd = async (from: string, to: string, offset: number) => {
    try {
      if (token) {
        const fetchedSessions: Session[] = await getTutorWeeklySessionsByStartEndDate(
          from,
          to,
          offset,
          token,
        );
        if (fetchedSessions) {
          const filteredSortedSessions = filterAndSortSessions(fetchedSessions);
          setWeeklySessions(filteredSortedSessions);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (selectedDates && selectedDates.length > 0 && tutorByIesId?.iesId) {
      getWeeklySessionsByTutorIdStartEnd(
        selectedDates[0].format('YYYY-MM-DD'),
        selectedDates[selectedDates.length - 1].format('YYYY-MM-DD'),
        deviceOffset,
      );
    }
  }, [selectedDates, tutorByIesId]);

  // Initialize with the current week's dates
  useEffect(() => {
    const startOfWeek = getCurrWeekMonday(dayjs());
    setSelectedDates(getWeekDates(startOfWeek));
  }, []);

  const isPreviousArrowBtnDisabled = (): boolean => {
    const prevMonday = getPreviousWeekMonday(dayjs(selectedDates[0]));
    const firstMonday = getCurrWeekMonday(dayjs());
    return prevMonday.isSame(firstMonday, 'day') || prevMonday.isAfter(firstMonday, 'day');
  };

  // Handle week navigation
  const navigateWeek = (direction: 'previous' | 'next') => {
    if (direction === 'previous') {
      const prevMonday = getPreviousWeekMonday(dayjs(selectedDates[0]));
      const firstMonday = getCurrWeekMonday(dayjs());
      if (prevMonday.isSame(firstMonday, 'day') || prevMonday.isAfter(firstMonday, 'day')) {
        setSelectedDates(getWeekDates(prevMonday));
      }
    }
    if (direction === 'next') {
      const nextMonday = getNextMonday(selectedDates[0]);
      setSelectedDates(getWeekDates(nextMonday));
    }
  };

  return (
    <section className="schedule-section content-wrapper">
      {tutorByIesId ? (
        <>
          <div className="section-title">
            <h5>Your upcoming classes</h5>
          </div>
          <div className="schedule-table">
            <TableHeader
              navigateWeek={navigateWeek}
              selectedDates={selectedDates.map((date) => date.toDate())}
              isPreviousArrowBtnDisabled={isPreviousArrowBtnDisabled}
            />
            <div className="table-body-wrapper">
              {selectedDates.map((date) =>
                !isBeforeToday(date.toDate()) ? (
                  <React.Fragment key={`fragment-${date.format('YYYY-MM-DD')}`}>
                    <TableDateRow date={date.toDate()} key={date.format('YYYY-MM-DD')} />
                    {weeklySessions.length > 0 &&
                    weeklySessions.filter((session) => dayjs(session.date).isSame(date, 'day')) && // filter out past days
                    weeklySessions.filter((session) => dayjs(session.date).isSame(date, 'day'))
                      .length > 0 ? (
                      weeklySessions
                        .filter((session) => dayjs(session.date).isSame(date, 'day'))
                        .map((dailySession) => (
                          <TableSessionRow session={dailySession} key={dailySession.id} />
                        ))
                    ) : (
                      <div className="empty-row">
                        <p className="b2-regular">No classes scheduled</p>
                      </div>
                    )}
                  </React.Fragment>
                ) : null,
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="loader">
          <OvalLoader />
        </div>
      )}
    </section>
  );
}

export default Schedule;
